import React from "react"
import { VStack, Text, HStack, Button, Box } from "@chakra-ui/react"
import { Link } from "@hydrologiq/shared/components/Link"
import { Alert } from "@hydrologiq/shared/chakra/Alert"
import AssetLink from "../asset/AssetLink"

interface AssetItem {
  id: string
  name: string
}

interface UnavailableAssetsWarningProps {
  assets: AssetItem[]
  onRemove: (assetIds: string[]) => void
  onViewCalendar: () => void
}

/**
 * Warning banner showing assets that are no longer available for selected dates
 * Provides actions to remove unavailable assets or view calendar
 * Pure presentational component with no business logic
 */
const UnavailableAssetsWarning: React.FC<UnavailableAssetsWarningProps> = ({ assets, onRemove, onViewCalendar }) => {
  if (assets.length === 0) {
    return null
  }

  const MAX_DISPLAY = 5
  const displayAssets = assets.slice(0, MAX_DISPLAY)
  const remainingCount = assets.length - MAX_DISPLAY

  const handleRemoveAll = () => {
    onRemove(assets.map((a) => a.id))
  }
  const title = assets.length === 1 ? "Asset no longer Available" : "Assets no longer Available"
  const message =
    assets.length === 1
      ? "Asset is no longer available for the selected dates:"
      : `${assets.length} assets are no longer available for the selected dates:`

  return (
    <Alert status="error" title={title} color="primary">
      <VStack align="stretch" gap={2}>
        <Text fontSize="sm">{message}</Text>
        <VStack align="stretch" gap={1} pl={4}>
          <Box as="ul" listStyleType="circle">
            {displayAssets.map((asset) => (
              <li key={asset.id}>
                <AssetLink asset={asset} openExternal={true} />
              </li>
            ))}
          </Box>

          {remainingCount > 0 && (
            <Text fontSize="sm" fontStyle="italic">
              and {remainingCount} more
            </Text>
          )}
        </VStack>
        <HStack gap={2} mt={2}>
          <Button size="sm" variant="hydrologiq_primary" onClick={handleRemoveAll}>
            Remove
          </Button>
          <Button size="sm" variant="hydrologiq_primary" onClick={onViewCalendar}>
            View Calendar
          </Button>
        </HStack>
      </VStack>
    </Alert>
  )
}

export default UnavailableAssetsWarning
